import { FunctionComponent, useEffect, useLayoutEffect, useState } from 'react';
import { useMount } from 'react-use';
import { useProvider } from 'wagmi';
import { ethers } from 'ethers';
import { OnlyBotMetadata } from '@animavirtuality/onlybots-core';
import { buildApiUrl, IPFS_MIRROR, tokenContractAddress } from '@/lib/globals';
import OnlyBotsToken from '@/contracts/OnlyBotsToken.json';

const IPFS = 'ipfs://';

type RequiredMetadata = {
    name: string;
    status: string | null;
};

type TokenPreviewProps = {
    tokenId: string;
};

const TokenPreview: FunctionComponent<TokenPreviewProps> = ({ tokenId }) => {
    const provider = useProvider();
    const [metadata, setMetadata] = useState<RequiredMetadata | null>(null);

    const images = {
        x1: `${IPFS_MIRROR}0-custom/onlybots-cover-v2-300/${tokenId}.webp`,
        x2: `${IPFS_MIRROR}0-custom/onlybots-cover-v2-600/${tokenId}.webp`,
        x3: `${IPFS_MIRROR}0-custom/onlybots-cover-v2-900/${tokenId}.webp`,
    };

    useEffect(() => {
        const contract = new ethers.Contract(tokenContractAddress, OnlyBotsToken.abi, provider);
        contract
            .tokenURI(tokenId)
            .then((uri: string) => {
                if (!uri.startsWith(IPFS)) {
                    throw new Error(`Invalid URI: ${uri}`);
                }

                const path = uri.slice(IPFS.length);
                const tokenJsonURI = new URL(`${IPFS_MIRROR}${path}`);
                return fetch(tokenJsonURI);
            })
            .then((response: Response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch token JSON`);
                }
                return response.json();
            })
            .then((data: OnlyBotMetadata) => {
                const status = data.attributes.find((attribute) => attribute.trait_type === 'Status');
                setMetadata({
                    name: data.name,
                    status: status?.value?.toString() ?? null,
                });
            })
            .catch(console.error);
    }, [tokenId, provider]);
    return (
        <a
            href={`/${tokenId}`}
            sx={{
                display: 'block',
                width: '300px',
                lineHeight: 1.3,
                color: 'rgb(112, 108, 160)',
                textDecoration: 'none',
                '&:visited': {
                    color: 'rgb(112, 108, 160)',
                },
            }}
        >
            <div sx={{ width: '100%', aspectRatio: '1 / 1', lineHeight: 0, marginBottom: '8px' }}>
                <img
                    src={images.x1}
                    srcSet={`${images.x1} 1x, ${images.x2} 2x`}
                    alt={metadata?.name ?? 'Loading description...'}
                    sx={{ width: '100%', height: '100%' }}
                />
            </div>
            <div sx={{ display: 'flex', fontSize: '18px', fontWeight: 'bold' }}>
                <span>{metadata?.name ?? '\u00A0'}</span>
                <span sx={{ marginLeft: 'auto' }}># {tokenId}</span>
            </div>
            <span sx={{ opacity: 0.6, fontSize: '16px', fontWeight: 'regular' }}>{metadata?.status ?? '\u00A0'}</span>
        </a>
    );
};

export const Talk: FunctionComponent = () => {
    const [tokenIds, setTokenIds] = useState<string[]>([]);

    useMount(() => {
        const url = buildApiUrl('/personas/list');
        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch token IDs');
                }
                return response.json();
            })
            .then((data) => {
                setTokenIds(data.ids);
            })
            .catch(console.error);
    });

    useLayoutEffect(() => {
        document.body.style.setProperty('background-color', '#d2cfed', 'important');
        let backup: Element | null = null;
        document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
            if (!backup) {
                backup = meta;
            }
            document.head.removeChild(meta);
        });
        const meta = document.createElement('meta');
        meta.name = 'theme-color';
        meta.content = '#d2cfed';
        document.head.appendChild(meta);

        return () => {
            document.body.style.removeProperty('background-color');
            document.head.removeChild(meta);
            if (backup) {
                document.head.appendChild(backup);
            }
        };
    });

    return (
        <div
            sx={{
                color: 'rgb(112, 108, 160)',
                fontFamily: 'helvetica, arial, sans-serif',
                minHeight: '100vh',
                paddingTop: '24px',
                paddingX: '16px',
                paddingBottom: '48px',
                width: '100%',
            }}
        >
            <div
                sx={{
                    width: '300px',
                    marginX: 'auto',

                    '@media screen and (min-width: 680px)': {
                        width: '650px',
                    },

                    '@media screen and (min-width: 1030px)': {
                        width: '1000px',
                    },
                }}
            >
                <h1 sx={{ fontSize: '64px', fontWeight: 'bold', marginTop: 0, marginBottom: '24px' }}>Get Loud!</h1>
                <p
                    sx={{
                        fontSize: '20px',
                        fontWeight: 'regular',
                        lineHeight: 1.25,
                        marginTop: 0,
                        marginBottom: '80px',
                    }}
                >
                    Scan their QR or tap their picture on your phone to talk to any of these Onlybots. Each with unique
                    attitudes, stories, and secrets. New Onlybots are added frequently.
                </p>
                <div
                    sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '50px', marginTop: '36px' }}
                >
                    {tokenIds.map((tokenId) => (
                        <TokenPreview key={tokenId} tokenId={tokenId} />
                    ))}
                </div>
            </div>
        </div>
    );
};
